import { useArchetypeVehicles } from "@src/api/useArchetypeVehicles";
import { useCommercialChargers } from "@src/api/useCommercialChargers";
import { useCommercialVehicles } from "@src/api/useCommercialVehicles";
import useProjectIncentives from "@src/api/useProjectIncentives";
import calcIdealRateName from "@src/calcs/calcIdealRateName";
import calcTotalWeeklyLoadProfile from "@src/calcs/calcTotalWeeklyLoadProfile";
import defaults from "@src/context/defaults";
import { useComputeChargerSets } from "@src/hooks/useComputeChargerSets";
import { useComputeRateInfo } from "@src/hooks/useComputeRateInfo";
import { useComputeVehicleSets } from "@src/hooks/useComputeVehicleSets";
import { IApplicationInputs } from "@src/types/inputs";
import { Vehicle } from "@src/types/vehicle";
import { PropsWithChildren, useCallback, useMemo, useReducer } from "react";
import InputContext from "../../context";

const CACHE_KEY = "@sce/fleets/inputs";

const parse = (item: string | null) => {
  return item ? JSON.parse(item) : {};
};

const DEFAULT_VALUES = { ...defaults, ...parse(localStorage.getItem(CACHE_KEY)) };

const reducer = (current: IApplicationInputs, changed: Partial<IApplicationInputs>) => {
  const newVehicles = Array.isArray(changed.vehicles)
    ? [...current.vehicles, ...changed.vehicles, ...(changed.customVehicles || [])]
    : current.vehicles;

  const newChargers = Array.isArray(changed.chargers)
    ? [...current.chargers, ...changed.chargers, ...(changed.customChargers || [])]
    : current.chargers;

  const newChargingFacilityType = changed.chargingFacilityType
    ? changed.chargingFacilityType
    : current.chargingFacilityType;

  const newInputs = {
    ...current,
    ...changed,
    chargingFacilityType: newChargingFacilityType,
    vehicles: newVehicles as Vehicle[],
    chargers: newChargers,
  };

  const {
    chargers: _chargers,
    vehicles: _vehicles,
    archetypes: _archetypes,
    incentives: _incentives,

    isPrinting: _isPrinting,
    ...rest
  } = newInputs;
  localStorage.setItem(CACHE_KEY, JSON.stringify(rest));
  return newInputs;
};

const Inputs = ({ children }: PropsWithChildren) => {
  const [inputs, dispatch] = useReducer(reducer, DEFAULT_VALUES);

  const { items: chargers, get: _getChargers, isLoaded: isLoadedChargers } = useCommercialChargers(false);
  const { items: archetypes, get: _getArchetypes, isLoaded: isLoadedArchetypes } = useArchetypeVehicles(false);
  const { items: vehicles, get: _getVehicles, isLoaded: isLoadedVehicles } = useCommercialVehicles(false);

  const { combinedIncentives: incentives, isLoaded: isLoadedProjectIncentives } = useProjectIncentives(inputs, false);

  const computeChargerSets = useComputeChargerSets();
  const computeVehicleSets = useComputeVehicleSets();
  const computeRateInfo = useComputeRateInfo();

  // Commercial incentives are loaded by project incentives
  // const { incentives, get, isLoaded: isLoadedCommercialIncentives } = useCommercialIncentives(false);

  const isLoaded = useMemo(
    () => [isLoadedChargers, isLoadedVehicles, isLoadedArchetypes, isLoadedProjectIncentives].every(Boolean),
    [isLoadedArchetypes, isLoadedChargers, isLoadedProjectIncentives, isLoadedVehicles],
  );

  const setInput = useCallback((changed: Partial<IApplicationInputs>) => {
    dispatch(changed);
  }, []);

  const value = useMemo(() => {
    const computedChargerSets = computeChargerSets(chargers, inputs.chargerSets);
    const computedVehicleSets = computeVehicleSets(
      vehicles,
      chargers,
      inputs.customVehicles,
      inputs.customChargers,
      inputs.vehicleSets,
    );

    const weeklyLoadProfileValues = calcTotalWeeklyLoadProfile(inputs.chargers, inputs.vehicleSets);

    const idealRateName = calcIdealRateName(weeklyLoadProfileValues);

    return {
      ...inputs,
      idealRateName,
      archetypes,
      chargers,
      incentives,
      vehicles,
      chargerSets: computedChargerSets,
      vehicleSets: computedVehicleSets,
      ...computeRateInfo.chargerSets({
        chargerSets: computedChargerSets,
        chargingFacilityType: inputs.chargingFacilityType,
      }),
      ...computeRateInfo.vehicleSets({ vehicleSets: computedVehicleSets, chargers }),
      isLoaded,
      setInput,
      setInputs: dispatch,
    };
  }, [
    computeChargerSets,
    chargers,
    inputs,
    computeVehicleSets,
    vehicles,
    archetypes,
    incentives,
    computeRateInfo,
    isLoaded,
    setInput,
  ]);

  return <InputContext.Provider value={value as IApplicationInputs}>{children}</InputContext.Provider>;
};

export default Inputs;
